import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { api, setAuthHandlers } from "../lib/api.js";

/* ============================================================================
 * AuthContext — owns the signed-in user (`me`), the cap set, effective limits,
 * the daily-usage heartbeat, and the lock-out screen. This is the React home of
 * everything shell.js used to do in `Shell.mount`:
 *   - fetch /api/me, expose caps + effective_limits + usage
 *   - hide nav/controls by cap (consumers call hasCap)
 *   - 401 -> redirect to /login ; 423 -> lock screen
 *   - heartbeat ping every 30s while visible (non-admin, finite daily_minutes)
 *   - one-time-password redirect to /account when me.must_change
 * ========================================================================== */

const AuthCtx = createContext(null);

const HB_INTERVAL_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(null); // null | { resetEpoch }
  const hbTimer = useRef(null);
  const hbLast = useRef(0);

  const caps = new Set((me && me.caps) || []);

  const refresh = useCallback(async () => {
    try {
      const data = await api("/api/me");
      setMe(data);
      return data;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/logout", { method: "POST" }).catch(() => {});
    setMe(null);
    window.location.href = "/login";
  }, []);

  /* ---- daily usage heartbeat (ports shell.js startHeartbeat/beat) ---- */
  const stopHeartbeat = useCallback(() => {
    if (hbTimer.current) clearInterval(hbTimer.current);
    hbTimer.current = null;
  }, []);

  const beat = useCallback(async () => {
    if (document.hidden) return;
    const now = Math.floor(Date.now() / 1000);
    const delta = Math.max(1, Math.min(60, now - hbLast.current));
    hbLast.current = now;
    try {
      const u = await api("/api/heartbeat", {
        method: "POST",
        body: { delta_seconds: delta },
      });
      setMe((m) => (m ? { ...m, usage: u } : m));
      if (u && u.remaining_seconds === 0) setLocked({ resetEpoch: u.reset_epoch });
    } catch {
      /* 423 handled by api(); transient errors retried next tick */
    }
  }, []);

  const startHeartbeat = useCallback(
    (user) => {
      stopHeartbeat();
      if (!user || user.is_admin) return;
      const cap = ((user.effective_limits || {}).time || {}).daily_minutes;
      if (cap == null || cap < 0) return; // unlimited
      hbLast.current = Math.floor(Date.now() / 1000);
      hbTimer.current = setInterval(beat, HB_INTERVAL_MS);
    },
    [beat, stopHeartbeat],
  );

  /* Wire the api() global handlers once. */
  useEffect(() => {
    setAuthHandlers({
      unauthorized: () => {
        setMe(null);
        if (window.location.pathname !== "/login")
          window.location.href = "/login";
      },
      lockout: (resetEpoch) => setLocked({ resetEpoch }),
    });
  }, []);

  /* Initial load. */
  useEffect(() => {
    refresh();
  }, [refresh]);

  /* Manage heartbeat lifecycle off `me`. */
  useEffect(() => {
    if (!me) {
      stopHeartbeat();
      return;
    }
    const u = me.usage;
    if (u && u.daily_seconds >= 0 && u.remaining_seconds === 0) {
      setLocked({ resetEpoch: u.reset_epoch });
      stopHeartbeat();
    } else {
      startHeartbeat(me);
    }
    const onVis = () => {
      if (document.hidden) stopHeartbeat();
      else startHeartbeat(me);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopHeartbeat();
    };
  }, [me, startHeartbeat, stopHeartbeat]);

  const value = {
    me,
    caps,
    loading,
    locked,
    refresh,
    logout,
    hasCap: (spec) => {
      if (!spec) return true;
      const need = String(spec).split(",").filter(Boolean);
      return need.length === 0 || need.some((c) => caps.has(c));
    },
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
